import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
        Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    }

    private handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center bg-white px-6 text-center">
                    <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-red-50">
                        <AlertTriangle size={36} className="text-red-500" />
                    </div>

                    <h1 className="mb-2 text-xl font-bold text-gray-900">
                        Vaya, algo ha ido mal
                    </h1>
                    <p className="mb-6 text-sm text-gray-500">
                        Ha ocurrido un error inesperado. Por favor, recarga la
                        página para intentarlo de nuevo.
                    </p>

                    <div className={`mb-6 w-full rounded-xl bg-red-50 p-4 text-left ${this.state.error?.message ? 'block' : 'hidden'}`}>
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-red-400">
                            Detalle técnico
                        </p>
                        <p className="wrap-break-word font-mono text-xs text-red-600">
                            {this.state.error?.message || '\u00A0'}
                        </p>
                    </div>

                    <button
                        onClick={this.handleReload}
                        className="flex items-center gap-2 rounded-2xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:bg-indigo-700 active:scale-[0.98]"
                    >
                        <RefreshCw size={16} />
                        Recargar página
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}
